import { useState, useEffect } from 'react';
import {
  Activity,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Info,
  Layers,
  Globe,
  Gauge,
  ToggleLeft,
  ToggleRight,
  Zap,
  Target,
  ArrowUpRight,
  Wind,
  Eye
} from 'lucide-react';
import AnalyticsConsentBanner from '../components/AnalyticsConsentBanner';
import { useAnalytics } from '../hooks/useAnalytics';
import { BrandNav, SiteFooter, ContactCTA } from '../components/SiteChrome';
import { AckGate, useAcknowledgement } from '../components/AckGate';
import { MARKETS } from '../lib/brand';
import { ShareReadButton } from '../components/ShareReadButton';
import { GuidedTour, hasSeenTour } from '../components/GuidedTour';
import { MarketButtons } from '../components/MarketButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;



const TRADE_STATE_STYLES = {
  'Constructive': { color: 'text-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/20', dot: 'bg-emerald-400' },
  'Building Strength': { color: 'text-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/20', dot: 'bg-emerald-400' },
  'Fragile Rally': { color: 'text-yellow-400', bg: 'bg-yellow-400/10 border-yellow-400/20', dot: 'bg-yellow-400' },
  'Under Pressure': { color: 'text-orange-400', bg: 'bg-orange-400/10 border-orange-400/20', dot: 'bg-orange-400' },
  'Extended Risk': { color: 'text-red-400', bg: 'bg-red-400/10 border-red-400/20', dot: 'bg-red-400' },
  'Breakdown Risk': { color: 'text-red-500', bg: 'bg-red-500/10 border-red-500/20', dot: 'bg-red-500' },
};

const ENGINE_LABELS = {
  energy: { icon: Zap, name: 'Energy' },
  pressure: { icon: Gauge, name: 'Pressure' },
  position: { icon: Target, name: 'Position' },
  momentum: { icon: ArrowUpRight, name: 'Momentum' },
  environment: { icon: Wind, name: 'Environment' },
};

const ENGINE_LABEL_COLORS = {
  'Efficient Move': 'text-emerald-400', 'Supported Move': 'text-emerald-400',
  'Weak Move': 'text-orange-400', 'Exhausted Move': 'text-red-400',
  'Low Pressure': 'text-emerald-400', 'Building Pressure': 'text-yellow-400',
  'High Pressure': 'text-orange-400', 'Breakdown Risk': 'text-red-400',
  'Advantage': 'text-emerald-400', 'Neutral': 'text-zinc-300',
  'Fragile': 'text-orange-400', 'Extended': 'text-red-400',
  'Building': 'text-emerald-400', 'Sustained': 'text-emerald-400',
  'Stalling': 'text-yellow-400', 'Reversing': 'text-red-400',
  'Supportive': 'text-emerald-400', 'Mixed': 'text-yellow-400',
  'Headwind': 'text-red-400',
};

const CONFIDENCE_COLORS = {
  'High': 'text-emerald-400',
  'Moderate': 'text-yellow-400',
  'Fragile': 'text-red-400',
};

const getRiskColor = (level) => {
  switch (level?.toLowerCase()) {
    case 'low': return 'text-emerald-400';
    case 'moderate': return 'text-yellow-400';
    case 'elevated': return 'text-orange-400';
    case 'high': return 'text-red-400';
    default: return 'text-zinc-400';
  }
};

const getRiskDotColor = (level) => {
  switch (level?.toLowerCase()) {
    case 'low': return 'bg-emerald-400';
    case 'moderate': return 'bg-yellow-400';
    case 'elevated': return 'bg-orange-400';
    case 'high': return 'bg-red-400';
    default: return 'bg-zinc-600';
  }
};

const getRiskBg = (level) => {
  switch (level?.toLowerCase()) {
    case 'low': return 'bg-emerald-400/10 border-emerald-400/20';
    case 'moderate': return 'bg-yellow-400/10 border-yellow-400/20';
    case 'elevated': return 'bg-orange-400/10 border-orange-400/20';
    case 'high': return 'bg-red-400/10 border-red-400/20';
    default: return 'bg-zinc-800/50 border-zinc-700';
  }
};

const getRiskDots = (level) => {
  const levels = { low: 1, moderate: 2, elevated: 3, high: 4 };
  const filled = levels[level?.toLowerCase()] || 0;
  return Array.from({ length: 4 }, (_, i) => i < filled);
};

const getSectorColor = (condition) => {
  switch (condition) {
    case 'Strengthening': return 'text-emerald-400';
    case 'Weakening': return 'text-red-400';
    default: return 'text-zinc-400';
  }
};

const getValueColor = (raw) => {
  if (raw > 0) return 'text-emerald-400';
  if (raw < 0) return 'text-red-400';
  return 'text-zinc-400';
};

// ===== COMPONENTS =====

function Panel({ title, icon: Icon, children, testId }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-brand" />}
        <span className="text-xs font-mono uppercase tracking-wider text-zinc-400">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function MetricRow({ label, value, subtext, colorClass = 'text-white' }) {
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-zinc-800/50 last:border-0">
      <span className="text-sm text-zinc-500">{label}</span>
      <div className="text-right">
        <span className={`font-mono text-sm font-medium ${colorClass}`}>{value}</span>
        {subtext && <p className="text-[10px] text-zinc-600 mt-0.5">{subtext}</p>}
      </div>
    </div>
  );
}

function EngineBar({ name, icon: Icon, label, score }) {
  const colorClass = ENGINE_LABEL_COLORS[label] || 'text-zinc-400';
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-zinc-800/30 last:border-0">
      <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
      <span className="text-xs text-zinc-500 w-24 flex-shrink-0">{name}</span>
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${score > 60 ? 'bg-red-400/70' : score > 45 ? 'bg-yellow-400/70' : 'bg-emerald-400/70'}`}
          style={{ width: `${Math.min(score, 100)}%` }}
        />
      </div>
      <span className={`font-mono text-xs font-medium w-32 text-right ${colorClass}`}>{label}</span>
    </div>
  );
}

export default function AnalyzePage() {
  const [params] = useState(() => new URLSearchParams(window.location.search));
  const [market, setMarket] = useState(() => (MARKETS.some((m) => m.code === params.get('market')) ? params.get('market') : ''));
  const [ticker, setTicker] = useState(() => (params.get('ticker') || '').toUpperCase().slice(0, 10));
  const { acknowledged, acknowledge } = useAcknowledgement();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [tourDone, setTourDone] = useState(hasSeenTour);
  const { track } = useAnalytics();

  const canAnalyze = market && ticker.trim() && acknowledged;

  useEffect(() => {
    track('page_view', { ui_surface: 'analysis_page' });
  }, [track]);

  const runAnalysis = async () => {
    if (!canAnalyze) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticker: ticker.toUpperCase().trim(), market, include_ai: true }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Analysis failed');
      setAnalysis(data);
      setShowResults(true);
      track('analysis_run', { market, ui_surface: 'analysis_page' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetAnalysis = () => {
    setShowResults(false);
    setAnalysis(null);
    setTicker('');
  };

  // ===================== LANDING VIEW =====================
  if (!showResults) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <BrandNav />

        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            <div className="text-center mb-10">
              <h1 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight" data-testid="hero-title">
                Market Intelligence, Simplified
              </h1>
              <p className="text-zinc-500 text-sm">
                Structured interpretation of market behaviour under pressure.
              </p>
            </div>

            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-5 space-y-5">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">Market</label>
                <MarketButtons value={market} onChange={setMarket} prefix="analyze" />
              </div>

              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">Ticker Symbol</label>
                <input
                  type="text"
                  value={ticker}
                  onChange={(e) => setTicker(e.target.value.toUpperCase())}
                  placeholder="e.g. AAPL, BHP, TSLA"
                  className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-brand/50 font-mono transition-colors"
                  maxLength={10}
                  data-testid="ticker-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') runAnalysis(); }}
                />
              </div>

              <AckGate acknowledged={acknowledged} onAcknowledge={acknowledge} />

              {error && (
                <div className="bg-red-500/5 border border-red-500/20 rounded-md p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}

              <button
                onClick={runAnalysis}
                disabled={!canAnalyze || loading}
                className="w-full bg-brand hover:bg-brand-light text-black font-bold text-sm py-3 rounded-md transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-brand"
                data-testid="analyze-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analysing...
                  </span>
                ) : (
                  'Run Analysis'
                )}
              </button>
            </div>

            <div className="mt-5 flex items-start gap-2 text-[11px] text-zinc-600 px-1">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <p>
                Analysis interprets price behaviour, pressure, and market context
                to produce structured intelligence. All outputs are descriptive only.
              </p>
            </div>
          </div>
        </main>

        <SiteFooter />
        {tourDone && <AnalyticsConsentBanner />}
        <GuidedTour onDone={() => setTourDone(true)} />
      </div>
    );
  }

  // ===================== RESULTS DASHBOARD =====================
  const risk = analysis.global_risk || {};
  const riskDots = getRiskDots(risk.level);
  const sectors = analysis.sector_conditions || {};
  const sectorEntries = Object.entries(sectors);
  const macro = analysis.macro_conditions || {};
  const assetRisk = analysis.asset_risk_profile || {};
  const cri = analysis.risk_index || {};
  const engines = analysis.engines || {};
  const tradeState = analysis.trade_state || 'Unknown';
  const stateStyle = TRADE_STATE_STYLES[tradeState] || TRADE_STATE_STYLES['Under Pressure'];
  const confidence = analysis.confidence || {};
  const falseMove = analysis.false_move || {};
  const tradeDna = analysis.trade_dna || null;
  const scenarioMatch = analysis.scenario_match || null;

  return (
    <div className="min-h-screen bg-black pb-28 sm:pb-16">
      {/* Nav */}
      <BrandNav width="max-w-4xl">
        <button
          onClick={() => setAdvancedMode(!advancedMode)}
          className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-brand transition-colors"
          data-testid="mode-toggle"
        >
          {advancedMode ? <ToggleRight className="w-4 h-4 text-brand" /> : <ToggleLeft className="w-4 h-4" />}
          <span className="hidden sm:inline">{advancedMode ? 'Advanced' : 'Basic'}</span>
        </button>
        <button
          onClick={resetAnalysis}
          className="text-xs border border-zinc-700 px-3 py-1.5 rounded-md hover:border-brand/60 text-zinc-400 hover:text-white transition-all"
          data-testid="new-analysis-btn"
        >
          New<span className="hidden sm:inline"> Analysis</span>
        </button>
      </BrandNav>

      <main className="max-w-4xl mx-auto px-4 pt-6">
        {/* === TRADE STATE HEADER === */}
        <div className={`border rounded-lg p-5 mb-5 ${stateStyle.bg}`} data-testid="trade-state-header">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-2.5 h-2.5 rounded-full ${stateStyle.dot}`} />
                <span className={`text-xl font-bold font-mono ${stateStyle.color}`}>{tradeState}</span>
              </div>
              <p className="text-xs text-zinc-400">
                {analysis.ticker} &middot; {analysis.asset_name} &middot; ${analysis.current_price?.toFixed(2)}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-[10px] font-mono uppercase text-zinc-600">Confidence</p>
                <p className={`text-sm font-bold font-mono ${CONFIDENCE_COLORS[confidence.label] || 'text-zinc-400'}`}>
                  {confidence.label || 'N/A'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-mono uppercase text-zinc-600">Risk</p>
                <p className={`text-sm font-bold font-mono ${getRiskColor(risk.level)}`}>
                  {risk.level || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* === WHAT MATTERED (Verdict) === */}
        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 mb-5" data-testid="verdict-panel">
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mb-2">What Mattered</p>
          <p className="text-sm text-zinc-200 leading-relaxed">{analysis.verdict || 'N/A'}</p>
        </div>

        {/* === 5-ENGINE BREAKDOWN === */}
        <Panel title="Engine Breakdown" icon={Activity} testId="panel-engines">
          {Object.entries(engines).map(([key, eng]) => {
            const meta = ENGINE_LABELS[key] || { icon: Activity, name: key };
            return <EngineBar key={key} name={meta.name} icon={meta.icon} label={eng.label} score={eng.score} />;
          })}
        </Panel>

        <div className="space-y-4 mt-4">
          {/* === FALSE MOVE DETECTOR === */}
          {falseMove.severity > 0 && (
            <div className={`border rounded-lg p-4 ${falseMove.severity >= 50 ? 'bg-red-500/5 border-red-500/20' : 'bg-yellow-400/5 border-yellow-400/20'}`} data-testid="false-move-detector">
              <div className="flex items-center gap-2 mb-2">
                <Eye className={`w-4 h-4 ${falseMove.severity >= 50 ? 'text-red-400' : 'text-yellow-400'}`} />
                <span className="text-xs font-mono uppercase tracking-wider text-zinc-400">False Move Detector</span>
                <span className={`text-xs font-mono ml-auto ${falseMove.severity >= 50 ? 'text-red-400' : 'text-yellow-400'}`}>
                  {falseMove.label}
                </span>
              </div>
              {falseMove.flags && falseMove.flags.map((flag, i) => (
                <p key={i} className="text-xs text-zinc-400 flex items-start gap-1.5 mt-1">
                  <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5 text-zinc-500" />
                  {flag}
                </p>
              ))}
            </div>
          )}

          {/* === WHAT TO MONITOR === */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid="what-to-monitor">
            <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mb-2">Conditions to Monitor</p>
            <p className="text-sm text-zinc-300">{analysis.what_to_monitor || 'N/A'}</p>
          </div>

          {/* === SCENARIO MATCH === */}
          {scenarioMatch && scenarioMatch.match_level !== 'Insufficient History' && (
            <div className={`border rounded-lg p-4 ${
              scenarioMatch.match_level === 'Strong' ? 'bg-brand/5 border-brand/20' :
              scenarioMatch.match_level === 'Partial' ? 'bg-yellow-400/5 border-yellow-400/20' :
              'bg-zinc-800/30 border-zinc-700'
            }`} data-testid="scenario-match">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-brand" />
                  <span className="text-xs font-mono uppercase tracking-wider text-zinc-400">Scenario Match</span>
                </div>
                <span className={`text-sm font-bold font-mono ${
                  scenarioMatch.match_level === 'Strong' ? 'text-brand' :
                  scenarioMatch.match_level === 'Partial' ? 'text-yellow-400' :
                  'text-zinc-500'
                }`}>
                  {scenarioMatch.match_level}
                </span>
              </div>
              <p className="text-xs text-zinc-400">{scenarioMatch.detail}</p>
              {scenarioMatch.matched_states && scenarioMatch.matched_states.length > 0 && (
                <div className="flex gap-2 mt-2">
                  {scenarioMatch.matched_states.map((ms, i) => (
                    <span key={i} className="text-[10px] bg-zinc-800 px-2 py-0.5 rounded font-mono text-zinc-400">
                      {ms.state} ({ms.count}x)
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {scenarioMatch && scenarioMatch.match_level === 'Insufficient History' && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid="scenario-match-empty">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-zinc-600" />
                <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Scenario Match</span>
              </div>
              <p className="text-xs text-zinc-600">{scenarioMatch.detail}</p>
            </div>
          )}

          {/* === TRADE DNA === */}
          {tradeDna && tradeDna.total_reads > 0 && (
            <Panel title={`Trade DNA — ${tradeDna.total_reads} Read${tradeDna.total_reads !== 1 ? 's' : ''}`} icon={Activity} testId="panel-trade-dna">
              <div className="flex items-center justify-between py-2 border-b border-zinc-800/50">
                <span className="text-xs text-zinc-500">Behavioural Profile</span>
                <span className="text-sm font-mono font-medium text-brand">{tradeDna.behaviour}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-800/50">
                <span className="text-xs text-zinc-500">Dominant State</span>
                <span className={`text-sm font-mono font-medium ${(TRADE_STATE_STYLES[tradeDna.dominant_state] || {}).color || 'text-zinc-400'}`}>
                  {tradeDna.dominant_state}
                </span>
              </div>
              {tradeDna.metrics && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                    {[
                      { label: 'Breakout Reliability', value: tradeDna.metrics.breakout_reliability },
                      { label: 'Pressure Tolerance', value: tradeDna.metrics.pressure_tolerance },
                      { label: 'Trend Persistence', value: tradeDna.metrics.trend_persistence },
                      { label: 'Consistency Index', value: tradeDna.metrics.consistency_index },
                      { label: 'Env Dependency', value: tradeDna.metrics.environment_dependency },
                      { label: 'False Move Rate', value: tradeDna.metrics.false_move_rate },
                    ].map((m) => (
                      <div key={m.label} className="bg-black/40 rounded-md p-2.5 border border-zinc-800/30">
                        <p className="text-[10px] font-mono uppercase text-zinc-600 mb-1">{m.label}</p>
                        <p className="text-lg font-bold font-mono text-zinc-200">{m.value}%</p>
                      </div>
                    ))}
                  </div>
                  {tradeDna.state_distribution && Object.keys(tradeDna.state_distribution).length > 1 && (
                    <div className="mt-3 pt-3 border-t border-zinc-800/50">
                      <p className="text-[10px] font-mono uppercase text-zinc-600 mb-2">State History</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(tradeDna.state_distribution).map(([state, count]) => (
                          <span key={state} className="text-[10px] bg-zinc-800 px-2 py-0.5 rounded font-mono text-zinc-400">
                            {state}: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Panel>
          )}

          {/* === V1 PANELS (under Advanced) === */}
          {advancedMode && (
            <>
              {/* Market Conditions */}
              <Panel title="Market Conditions" icon={Activity} testId="panel-market-conditions">
                <MetricRow
                  label="Index Direction"
                  value={
                    analysis.market_conditions?.index?.change_pct != null
                      ? `${analysis.market_conditions.index.name}: ${analysis.market_conditions.index.change_pct >= 0 ? '+' : ''}${analysis.market_conditions.index.change_pct}%`
                      : 'Data unavailable'
                  }
                  subtext="current session"
                />
                <MetricRow label="Volatility Level" value={analysis.volatility?.level || 'N/A'} colorClass={getRiskColor(analysis.volatility?.level)} />
                <MetricRow label="Market Structure" value={analysis.market_conditions?.market_structure || 'N/A'} />
              </Panel>

              {/* Price Positioning */}
              <Panel title="Price Positioning" icon={BarChart3} testId="panel-price-positioning">
                <MetricRow label="Price vs 20DMA" value={analysis.price_position?.vs_20dma || 'N/A'} subtext="short-term average" colorClass={getValueColor(analysis.price_position?.vs_20dma_raw)} />
                <MetricRow label="Price vs 50DMA" value={analysis.price_position?.vs_50dma || 'N/A'} subtext="medium-term average" colorClass={getValueColor(analysis.price_position?.vs_50dma_raw)} />
                <MetricRow label="Range Position" value={analysis.price_position?.range_position || 'N/A'} />
              </Panel>

              {/* Volatility Profile */}
              <Panel title="Volatility Profile" icon={TrendingUp} testId="panel-volatility">
                <MetricRow label="30-day Comparison" value={analysis.volatility?.comparison || 'N/A'} />
                <MetricRow label="Volatility Trend" value={analysis.volatility?.trend ? analysis.volatility.trend.charAt(0).toUpperCase() + analysis.volatility.trend.slice(1) : 'N/A'} />
                <MetricRow label="Annualized Volatility" value={analysis.volatility?.current_annualized || 'N/A'} />
              </Panel>

              {/* Sector Conditions */}
              {sectorEntries.length > 0 && (
                <Panel title="Sector Conditions" icon={Layers} testId="panel-sector-conditions">
                  <div className="space-y-0">
                    <div className="flex justify-between text-[10px] font-mono uppercase text-zinc-600 pb-2 border-b border-zinc-800/50">
                      <span>Sector</span>
                      <span>Condition</span>
                    </div>
                    {sectorEntries.map(([sector, info]) => (
                      <div key={sector} className="flex justify-between items-center py-2 border-b border-zinc-800/30 last:border-0">
                        <span className="text-sm text-zinc-400">{sector}</span>
                        <span className={`text-sm font-mono ${getSectorColor(info.condition)}`}>{info.condition}</span>
                      </div>
                    ))}
                  </div>
                </Panel>
              )}

              {/* Macro + CRI */}
              <Panel title="Macro Conditions" icon={Globe} testId="panel-macro-conditions">
                <MetricRow label="Interest Rate Environment" value={macro.interest_rate_environment || 'N/A'} />
                <MetricRow label="Market Liquidity" value={macro.market_liquidity || 'N/A'} />
                <MetricRow label="Volatility Regime" value={macro.volatility_regime || 'N/A'} />
              </Panel>

              {cri.score != null && (
                <Panel title="Composite Risk Index" icon={Gauge} testId="panel-risk-index">
                  <div className="text-center py-3">
                    <p className={`text-4xl font-bold font-mono ${getRiskColor(cri.label)}`}>{cri.score}</p>
                    <p className={`text-sm font-mono mt-1 ${getRiskColor(cri.label)}`}>{cri.label}</p>
                  </div>
                  {cri.breakdown && (
                    <div className="mt-3 pt-3 border-t border-zinc-800/50 space-y-2">
                      <div className="flex justify-between text-xs text-zinc-500">
                        <span>Volatility</span><span className="font-mono">{cri.breakdown.volatility}</span>
                      </div>
                      <div className="flex justify-between text-xs text-zinc-500">
                        <span>Macro</span><span className="font-mono">{cri.breakdown.macro}</span>
                      </div>
                      <div className="flex justify-between text-xs text-zinc-500">
                        <span>Sector</span><span className="font-mono">{cri.breakdown.sector_dispersion}</span>
                      </div>
                    </div>
                  )}
                </Panel>
              )}
            </>
          )}

          {/* Intelligence Summary */}
          <Panel title="Intelligence Summary" icon={Info} testId="panel-summary">
            <div className="bg-black/40 rounded-md p-4 border border-zinc-800/30">
              <p className="text-sm text-zinc-300 leading-relaxed">
                {analysis.ai_insights || analysis.summary || 'Summary unavailable'}
              </p>
            </div>
            <p className="text-[10px] text-zinc-600 mt-3 text-center">
              AI-generated descriptive analysis &middot; No recommendations provided
            </p>
          </Panel>
        </div>

        {/* Metadata */}
        <div className="mt-4 text-center">
          <p className="text-[11px] text-zinc-600">
            {analysis.market} &middot; {new Date(analysis.analysis_timestamp).toLocaleString()}
            {analysis.data_freshness && analysis.data_freshness !== 'Live data' && (
              <span className="ml-2 text-zinc-500">&middot; {analysis.data_freshness}</span>
            )}
          </p>
        </div>

        <ShareReadButton readId={analysis.read_id} />
        <ContactCTA className="mt-6" source="analysis" ticker={analysis.ticker} />
      </main>

      <SiteFooter fixed />
    </div>
  );
}
